import React, { useRef, useEffect } from 'react';
import { 
  Utensils, Coffee, Salad, Soup, Pizza, IceCream, 
  Sandwich, Drumstick, Fish, Leaf, Wine, Cookie,
  ChefHat, Flame, Star
} from 'lucide-react';

// Icon mapping for category names
const CATEGORY_ICONS = {
  'popular': Star,
  'featured': Star,
  'appetizers': Salad,
  'appetizer': Salad,
  'starters': Salad,
  'soups': Soup,
  'soup': Soup,
  'pho': Soup,
  'noodle': Soup,
  'noodles': Soup,
  'rice': Utensils,
  'rice plates': Utensils,
  'entrees': ChefHat,
  'main': ChefHat,
  'mains': ChefHat,
  'chicken': Drumstick,
  'beef': Flame,
  'seafood': Fish,
  'fish': Fish,
  'vegetarian': Leaf,
  'vegan': Leaf,
  'salads': Salad,
  'salad': Salad,
  'sandwiches': Sandwich,
  'sandwich': Sandwich,
  'pizza': Pizza,
  'desserts': IceCream,
  'dessert': IceCream,
  'sweets': Cookie,
  'drinks': Coffee,
  'beverages': Coffee,
  'beverage': Coffee,
  'coffee': Coffee,
  'tea': Coffee,
  'wine': Wine,
  'alcohol': Wine,
  'beer': Wine,
};

/**
 * Get icon component for a category name
 */
function getCategoryIcon(categoryName) {
  const nameLower = categoryName.toLowerCase();
  
  // Check for exact match first
  if (CATEGORY_ICONS[nameLower]) {
    return CATEGORY_ICONS[nameLower];
  }
  
  // Check for partial match
  for (const [key, Icon] of Object.entries(CATEGORY_ICONS)) {
    if (nameLower.includes(key)) {
      return Icon;
    }
  }
  
  // Default icon
  return Utensils;
}

/**
 * CategoryNav - Vertical category navigation for kiosk menu
 * 
 * Features:
 * - Vertical scrollable list
 * - Icons + labels for each category
 * - Active state highlighting
 * - Smooth scroll on selection
 */
export default function CategoryNav({
  categories = [],
  activeCategory,
  onCategorySelect,
  className = '',
}) {
  const navRef = useRef(null);
  const activeRef = useRef(null);

  // Scroll active category into view
  useEffect(() => {
    if (activeRef.current && navRef.current) {
      activeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [activeCategory]);

  if (!categories.length) {
    return (
      <nav className={`w-64 bg-[#1a1f26] border-r border-white/10 ${className}`}>
        <div className="p-4 text-center text-gray-500">
          No categories available
        </div>
      </nav>
    );
  }

  return (
    <nav 
      ref={navRef}
      className={`
        w-64 bg-[#1a1f26] border-r border-white/10
        flex flex-col
        overflow-y-auto
        scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent
        ${className}
      `}
      role="navigation"
      aria-label="Menu categories"
    >
      <div className="p-3 space-y-2">
        {categories.map((category) => {
          const Icon = getCategoryIcon(category.name);
          const isActive = activeCategory === category.id;
          const itemCount = category.items?.length || 0;

          return (
            <button
              key={category.id}
              ref={isActive ? activeRef : null}
              onClick={() => onCategorySelect(category.id)}
              className={`
                w-full flex items-center gap-4 px-4 py-4
                rounded-xl transition-all duration-200
                min-h-[64px]
                ${isActive
                  ? 'bg-gradient-to-r from-[#f5a623] to-[#ff6b35] text-[#0f1419] shadow-lg shadow-[#f5a623]/20'
                  : 'bg-[#252b33] text-white/80 hover:bg-[#2d343d] hover:text-white'
                }
              `}
              aria-pressed={isActive}
              aria-label={`${category.name} - ${itemCount} items`}
            >
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                ${isActive ? 'bg-[#0f1419]/20' : 'bg-white/10'}
              `}>
                <Icon size={22} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-semibold text-base truncate">
                  {category.name}
                </div>
                <div className={`text-sm ${isActive ? 'text-[#0f1419]/70' : 'text-white/50'}`}>
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
